import { Navigate } from "react-router-dom";
import { Card, Button, Typography, Space, Spin } from "antd";
import { WindowsOutlined, LockOutlined } from "@ant-design/icons";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "@/config/msal";

const { Title, Text, Paragraph } = Typography;

export default function SignInPage() {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/share" replace />;
  }

  const handleSignIn = () => {
    // Prevent multiple login attempts
    if (inProgress !== InteractionStatus.None) {
      return;
    }
    instance.loginRedirect(loginRequest).catch((error) => {
      // Ignore interaction_in_progress errors - user just needs to wait
      if (error.errorCode !== 'interaction_in_progress') {
        console.error('Login error:', error);
      }
    });
  };

  // Show loading while authentication is in progress
  if (inProgress !== InteractionStatus.None) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "#F8F7F5",
          gap: 16,
        }}
      >
        <Spin size="large" />
        <Text type="secondary">Redirecting to Microsoft...</Text>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#F8F7F5",
        padding: 24,
      }}
    >
      {/* Logo */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 32,
        }}
      >
        <img
          src="/secondary-logo-gradient.png"
          alt="AIXA"
          style={{ width: 40, height: 40, objectFit: "contain" }}
        />
        <Title level={2} style={{ margin: 0, color: "#0F6E56", letterSpacing: "0.5px" }}>
          AIXA <span style={{ fontWeight: 400 }}>Share</span>
        </Title>
      </div>

      <Card
        style={{
          maxWidth: 400,
          width: "100%",
          borderRadius: 12,
          boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
        }}
        styles={{
          body: { padding: 32 },
        }}
      >
        <div style={{ textAlign: "center" }}>
          <Title level={3} style={{ marginBottom: 8 }}>
            Welcome back
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 32 }}>
            Sign in with your organization account to share documents securely.
          </Paragraph>

          <Button
            type="primary"
            size="large"
            icon={<WindowsOutlined />}
            onClick={handleSignIn}
            block
          >
            Sign in with Microsoft
          </Button>

          <div
            style={{
              marginTop: 24,
              padding: 12,
              background: "rgba(15, 110, 86, 0.04)",
              borderRadius: 8,
            }}
          >
            <Space direction="vertical" size={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <LockOutlined style={{ marginRight: 4 }} />
                Secure Sharing
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Your session will timeout after 15 minutes of inactivity.
              </Text>
            </Space>
          </div>
        </div>
      </Card>

      <Text
        type="secondary"
        style={{ marginTop: 32, fontSize: 12, textAlign: "center" }}
      >
        By signing in, you agree to the organization&apos;s
        <br />
        data handling and sharing policies.
      </Text>
    </div>
  );
}
